import type { BackgammonTurn } from '@chessmate/game-engine';
import type { RobotTurnRequest } from './robot-turn.js';

/** The slice of `Worker` the client needs — a fake satisfies it in tests. */
export interface RobotWorkerLike {
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void;
  postMessage(message: unknown): void;
}

export interface RobotReply {
  readonly id: number;
  readonly turn: BackgammonTurn;
}

let nextRequestId = 0;

/**
 * Post a request and hand the matching reply to `onReply`. Replies are matched
 * by id: a superseded request (undo/restart while the worker is still
 * searching) answers later, and must not be taken by a newer request's
 * listener — the caller's position guard cannot tell them apart, because it
 * compares against the newer closure.
 */
export function requestRobotTurn(
  worker: RobotWorkerLike,
  request: RobotTurnRequest,
  onReply: (turn: BackgammonTurn) => void,
): void {
  const id = ++nextRequestId;
  const handler = (event: MessageEvent<RobotReply>): void => {
    if (event.data.id !== id) return;
    worker.removeEventListener('message', handler as (event: MessageEvent) => void);
    onReply(event.data.turn);
  };
  worker.addEventListener('message', handler as (event: MessageEvent) => void);
  worker.postMessage({ id, request });
}
